"use client";
import type { ColorScale, ParkSkinAPIObject, Theme, ThemeBase } from "ipctg-inflow-types/InFlowAPIMisc";
import { vars } from "nativewind";

type BackgroundSpecial = {
  error: string;
  warning: string;
  success: string;
  muted: string;
  info: string;
};

type FocusRingIndicator = {
  primary: string;
  info: string;
  error: string;
};

export function themeToRecord(theme: Theme): Record<string, string> {
  const themeKeys = Object.keys(theme);
  const varsObj: { [key: string]: string } = {};

  for (const themeKey of themeKeys) {
    const colors = theme[themeKey as keyof Theme];
    const colorKeys = Object.keys(colors);
    if ("500" in colors) {
      for (const colorKey of colorKeys) {
        varsObj[`--color-${themeKey}-${colorKey}`] = colors[colorKey as keyof ColorScale] ?? "#000";
      }
    } else if ("warning" in colors) {
      for (const colorKey of colorKeys) {
        varsObj[`--color-${themeKey}-${colorKey}`] = colors[colorKey as keyof BackgroundSpecial];
      }
    } else {
      for (const colorKey of colorKeys) {
        varsObj[`--color-${themeKey}-${colorKey}`] = colors[colorKey as keyof FocusRingIndicator];
      }
    }
  }

  return vars(varsObj);
}

export function toGluestackTheme(themeBase: ThemeBase): {
  dark: Record<string, string>;
  light: Record<string, string>;
} {
  return {
    dark: themeToRecord(themeBase.dark),
    light: themeToRecord(themeBase.light),
  };
}

export const defaultTheme: ParkSkinAPIObject = {
  locationAddress: {
    address: "",
    coordinates: null,
    placeID: "",
  },
  locationName: "",
  logoURL: "",
  supportEmail: "",
  supportPhone: "",
  homepageHeader: "",
  theme: {
    dark: {
      secondary: {
        100: "#FCFCFC",
        200: "#F9F9F9",
        300: "#F0F0F0",
        400: "#E8E8E8",
        500: "#E1E1E1",
        600: "#D9D9D9",
        700: "#CECECE",
        800: "#BBBBBB",
        900: "#000000",
      },
      typography: {
        100: "#FCFCFC",
        200: "#F9F9F9",
        300: "#242424",
        400: "#E8E8E8",
        500: "#E1E1E1",
        600: "#D9D9D9",
        700: "#CECECE",
        800: "#BBBBBB",
        900: "#242424",
      },
      outline: {
        50: "#F3F3F3",
        100: "#E6E6E6",
        200: "#DDCDC7",
        300: "#D3D3D3",
        400: "#A5A3A3",
        500: "#8C8D8D",
        600: "#737474",
        700: "#535252",
        800: "#414141",
        900: "#272624",
      },
      background: {
        50: "#F6F6F6",
        100: "#fff",
        200: "#DCDCDC",
        300: "#D5D4D4",
        400: "#A2A3A3",
        500: "#8E8E8E",
        600: "#747474",
        700: "#535252",
        800: "#414040",
        900: "#272625",
      },
      focusRingIndicator: {
        error: "#000000",
        info: "#000000",
        primary: "#000000",
      },
      success: {
        50: "#CAFFE8",
        100: "#A2F1C0",
        200: "#84D3A2",
        300: "#66B584",
        400: "#489766",
        500: "#348352",
        600: "#2A7948",
        700: "#008000",
        800: "#166532",
        900: "#14532D",
      },
      tertiary: {
        50: "#FF11F1",
        100: "#FFFFFF",
        200: "#FF10D1",
        300: "#FF1041",
        400: "#FFFAE9",
        500: "#FEF4D4",
        600: "#FBEDC0",
        700: "#F7E58C",
        800: "#F1DD9A",
        900: "#EAD48A",
      },
      warning: {
        50: "#FFF9F5",
        100: "#FFE7D5",
        200: "#FECDAB",
        300: "#FDAE74",
        400: "#FB954B",
        500: "#E77828",
        600: "#D76C1F",
        700: "#B45A1A",
        800: "#824417",
        900: "#6C3813",
      },
      error: {
        20: "#df00001a",
        50: "#FEE2E2",
        100: "#DF0000",
        200: "#FCA5A5",
        300: "#F87171",
        400: "#EF4444",
        500: "#E63535",
        600: "#DC2626",
        700: "#B91C1C",
        800: "#992B1B",
        900: "#7F1D1D",
      },
      backgroundSpecial: {
        warning: "#FFF4EB",
        error: "#FEF1F1",
        muted: "#F7F8F7",
        success: "#EDFCE2",
        info: "#EBF8FE",
      },
      info: {
        50: "#C7EBFC",
        100: "#A2DDFA",
        200: "#7CCFF8",
        300: "#57C2F6",
        400: "#32B4F4",
        500: "#0DA6F2",
        600: "#0B8DCD",
        700: "#0973A8",
        800: "#075A83",
        900: "#05405D",
      },
      primary: {
        100: "#FEFDFB",
        200: "#FFFAE9",
        300: "#FFF4C0",
        400: "#FFEA9B",
        500: "#FFDF78",
        600: "#FCD274",
        700: "#E8C268",
        800: "#D7AA39",
        900: "#FFCB06",
      },
    },
    light: {
      secondary: {
        100: "#FCFCFC",
        200: "#F9F9F9",
        300: "#F0F0F0",
        400: "#E8E8E8",
        500: "#E1E1E1",
        600: "#D9D9D9",
        700: "#CECECE",
        800: "#BBBBBB",
        900: "#000000",
      },
      typography: {
        100: "#FCFCFC",
        200: "#F9F9F9",
        300: "#242424",
        400: "#E8E8E8",
        500: "#E1E1E1",
        600: "#D9D9D9",
        700: "#CECECE",
        800: "#BBBBBB",
        900: "#242424",
      },
      outline: {
        50: "#F3F3F3",
        100: "#E6E6E6",
        200: "#DDCDC7",
        300: "#D3D3D3",
        400: "#A5A3A3",
        500: "#8C8D8D",
        600: "#737474",
        700: "#535252",
        800: "#414141",
        900: "#272624",
      },
      background: {
        50: "#F6F6F6",
        100: "#fff",
        200: "#DCDCDC",
        300: "#D5D4D4",
        400: "#A2A3A3",
        500: "#8E8E8E",
        600: "#747474",
        700: "#535252",
        800: "#414040",
        900: "#272625",
      },
      focusRingIndicator: {
        error: "#000000",
        info: "#000000",
        primary: "#000000",
      },
      success: {
        50: "#CAFFE8",
        100: "#A2F1C0",
        200: "#84D3A2",
        300: "#66B584",
        400: "#489766",
        500: "#348352",
        600: "#2A7948",
        700: "#008000",
        800: "#166532",
        900: "#14532D",
      },
      tertiary: {
        50: "#FF11F1",
        100: "#FFFFFF",
        200: "#FF10D1",
        300: "#FF1041",
        400: "#FFFAE9",
        500: "#FEF4D4",
        600: "#FBEDC0",
        700: "#F7E58C",
        800: "#F1DD9A",
        900: "#EAD48A",
      },
      warning: {
        50: "#FFF9F5",
        100: "#FFE7D5",
        200: "#FECDAB",
        300: "#FDAE74",
        400: "#FB954B",
        500: "#E77828",
        600: "#D76C1F",
        700: "#B45A1A",
        800: "#824417",
        900: "#6C3813",
      },
      error: {
        20: "#df00001a",
        50: "#FEE2E2",
        100: "#DF0000",
        200: "#FCA5A5",
        300: "#F87171",
        400: "#EF4444",
        500: "#E63535",
        600: "#DC2626",
        700: "#B91C1C",
        800: "#992B1B",
        900: "#7F1D1D",
      },
      backgroundSpecial: {
        warning: "#FFF4EB",
        error: "#FEF1F1",
        muted: "#F7F8F7",
        success: "#EDFCE2",
        info: "#EBF8FE",
      },
      info: {
        50: "#C7EBFC",
        100: "#A2DDFA",
        200: "#7CCFF8",
        300: "#57C2F6",
        400: "#32B4F4",
        500: "#0DA6F2",
        600: "#0B8DCD",
        700: "#0973A8",
        800: "#075A83",
        900: "#05405D",
      },
      primary: {
        100: "#FEFDFB",
        200: "#FFFAE9",
        300: "#FFF4C0",
        400: "#FFEA9B",
        500: "#FFDF78",
        600: "#FCD274",
        700: "#E8C268",
        800: "#D7AA39",
        900: "#FFCB06",
      },
    },
  },
};
